import { useEffect, useMemo } from 'react';

import { useGetChatsQuery } from '../../services/chatAdministrationApi';
import { fleetGroupFilterSelectionChanged, useSelectedFleetGroupTagIds } from './fleetGroupFilterSlice';
import {
    filterChatsByFleetGroups,
    getValidSelectedFleetGroupTagIds,
    matchesSelectedFleetGroups,
} from './fleetGroupFilter';
import type { Channel } from '../chat/chatSlice';
import { useFeatureToggle } from '../../configuration/featureFlags/useFeatureToggle';
import { useAppDispatch } from '../../configuration/setup/hooks';

export const useFilteredChats = () => {
    const dispatch = useAppDispatch();
    const { data: chats = [], isLoading, isSuccess } = useGetChatsQuery();
    const storedSelectedTagIds = useSelectedFleetGroupTagIds();
    const showFleetGroupFilter = useFeatureToggle('showFleetGroupFilter', false);
    const validSelectedTagIds = useMemo(
        () => getValidSelectedFleetGroupTagIds(chats, storedSelectedTagIds),
        [chats, storedSelectedTagIds]
    );
    const selectedTagIds = showFleetGroupFilter ? validSelectedTagIds : [];

    useEffect(() => {
        if (!showFleetGroupFilter || !isSuccess || storedSelectedTagIds.length === validSelectedTagIds.length) {
            return;
        }

        dispatch(fleetGroupFilterSelectionChanged(validSelectedTagIds));
    }, [dispatch, isSuccess, showFleetGroupFilter, storedSelectedTagIds, validSelectedTagIds]);

    const filteredChats = useMemo(() => filterChatsByFleetGroups(chats, selectedTagIds), [chats, selectedTagIds]);

    return { chats, filteredChats, selectedTagIds, isLoading };
};

export const useFilteredChannels = (channels: Channel[]) => {
    const { chats, selectedTagIds } = useFilteredChats();

    return useMemo(() => {
        const chatsById = new Map(chats.map(chat => [chat.id, chat]));
        return channels.filter(channel => matchesSelectedFleetGroups(chatsById.get(channel.channelId), selectedTagIds));
    }, [channels, chats, selectedTagIds]);
};

export const useFilteredTotalUnreadMessageCount = (channels: Channel[]) => {
    const filteredChannels = useFilteredChannels(channels);

    return useMemo(
        () => filteredChannels.reduce((total, channel) => total + channel.unreadMessageCount, 0),
        [filteredChannels]
    );
};
